export const SHOW_GRACE_PERIOD_MS = 4 * 60 * 60 * 1000;

export interface EventSchedule {
  start_datetime?: string | null;
  local_date: string | null;
  local_time?: string | null;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Resolve wall time like PostgreSQL: use the later instant during a DST fold. */
function localTimestamp(date: string, time: string, timezone: string): number {
  let formatter = formatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
    });
    formatters.set(timezone, formatter);
  }
  const wallTime = Date.parse(`${date}T${time}Z`);
  const asWallTime = (instant: number) => {
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value }) => [type, value]));
    return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  };
  const offsets = new Set([-86400000, 0, 86400000].map((delta) => {
    const instant = Math.floor((wallTime + delta) / 1000) * 1000;
    return asWallTime(instant) - instant;
  }));
  const candidates = [...offsets].map((offset) => wallTime - offset);
  const matching = candidates.filter((instant) => asWallTime(instant) === Math.floor(wallTime / 1000) * 1000);
  // PostgreSQL moves a nonexistent spring-forward time forward through the gap.
  return Math.max(...(matching.length ? matching : candidates));
}

/** Actual calendar date, not the previous evening's display/listing date. */
export function eventExpiresAt(event: EventSchedule, timezone: string): number | null {
  const absoluteStart = event.start_datetime ? Date.parse(event.start_datetime) : NaN;
  if (Number.isFinite(absoluteStart)) return absoluteStart + SHOW_GRACE_PERIOD_MS;
  if (!event.local_date) return null;
  if (event.local_time) return localTimestamp(event.local_date, event.local_time, timezone) + SHOW_GRACE_PERIOD_MS;
  // No known start time: retain the listing for its whole local calendar day.
  const nextDay = new Date(`${event.local_date}T12:00:00Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  return localTimestamp(nextDay.toISOString().slice(0, 10), "00:00:00", timezone);
}

export function isEventInFeed(event: EventSchedule, timezone: string, now = new Date()): boolean {
  const expiry = eventExpiresAt(event, timezone);
  return expiry !== null && expiry > now.getTime();
}
